// "New order" in the top bar of a restaurant: one button on every page that starts
// a dine-in, takeaway or delivery order. Shortcut: N.
import { buttonClass, IconTile, Kbd, Menu, MenuItem } from '@ims/ui'
import { Bike, Plus, ShoppingBag, UtensilsCrossed } from '@ims/ui/icons'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSettings } from '../hooks.ts'
import { dialogOpen, isTyping } from '../lib/shortcuts.ts'
import { NewDeliveryDialog } from '../screens/deliveries.tsx'
import { NewTakeawayDialog } from '../screens/restaurant.tsx'
import { useCan } from '../workspace.tsx'

/**
 * A button that opens the three ways to start an order: Dine in (pick a table), Takeaway and
 * Delivery (only when delivery is on). Draws nothing for someone who cannot take orders.
 */
export function NewOrderMenu({
  label = 'New order',
  title,
  trigger,
  triggerClassName,
  align = 'end',
}: {
  /** The button's accessible name. */
  label?: string
  title?: string
  trigger: ReactNode
  triggerClassName: string
  align?: 'start' | 'end'
}) {
  const settings = useSettings()
  const canServe = useCan('waiter')
  const navigate = useNavigate()
  const [starting, setStarting] = useState<'takeaway' | 'delivery' | null>(null)
  if (!settings?.restaurant || !canServe) return null
  return (
    <>
      <Menu
        label={label}
        title={title}
        align={align}
        triggerClassName={triggerClassName}
        trigger={trigger}
      >
        <MenuItem
          icon={
            <IconTile area="kitchen" size="sm">
              <UtensilsCrossed className="size-[17px]" />
            </IconTile>
          }
          hint="Pick a table"
          onSelect={() => navigate('/tables')}
        >
          Dine in
        </MenuItem>
        <MenuItem
          icon={
            <IconTile area="take" size="sm">
              <ShoppingBag className="size-[17px]" />
            </IconTile>
          }
          onSelect={() => setStarting('takeaway')}
        >
          Takeaway
        </MenuItem>
        {settings.delivery_product_id && (
          <MenuItem
            icon={
              <IconTile area="insight" size="sm">
                <Bike className="size-[17px]" />
              </IconTile>
            }
            onSelect={() => setStarting('delivery')}
          >
            Delivery
          </MenuItem>
        )}
      </Menu>
      {starting === 'takeaway' && <NewTakeawayDialog onClose={() => setStarting(null)} />}
      {starting === 'delivery' && <NewDeliveryDialog onClose={() => setStarting(null)} />}
    </>
  )
}

/** "New order" in the top bar, on every page of a restaurant. Shortcut: N. */
export function NewOrder() {
  const settings = useSettings()
  const canServe = useCan('waiter')
  const box = useRef<HTMLDivElement>(null)
  const shown = !!settings?.restaurant && canServe

  // N opens the menu; the arrows and Enter choose from there.
  useEffect(() => {
    if (!shown) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'n' || e.defaultPrevented) return
      if (e.ctrlKey || e.metaKey || e.altKey || isTyping(e.target) || dialogOpen()) return
      const button = box.current?.querySelector('button')
      if (!button || button.getAttribute('aria-expanded') === 'true') return
      e.preventDefault()
      button.click()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [shown])

  if (!shown) return null
  return (
    <div ref={box}>
      <NewOrderMenu
        title="New order (N)"
        triggerClassName={buttonClass({ size: 'sm', className: 'gap-1.5 max-sm:px-2.5' })}
        trigger={
          <>
            <Plus className="size-4" aria-hidden />
            <span className="max-sm:sr-only">New order</span>
            <Kbd className="ml-1 border-on-fill/30! bg-on-fill/15! text-on-fill! max-lg:hidden">
              N
            </Kbd>
          </>
        }
      />
    </div>
  )
}
